import { useDialog } from '@globals/dialog/useDialogStore'
import theme, { colors } from '@globals/themes/defaultTheme'
import { Recognition } from '@kudos/http-client'
import Box from '@mui/material/Box'
import { SxProps } from '@mui/material/styles'
import BottomDrawer from '@shared/dialog/BottomDrawer'
import FormContextWrapper from '@shared/forms/FormContextWrapper'
import useQualities from '@shared/hooks/useQualities'
import useSendRecognitionForm, {
  RecognitionFormData,
} from '@tenant/recognition/RecognitionModal/sendRecognitionView/useSendRecognitionForm'
import { useSendRecognitionFormStore } from '@tenant/recognition/RecognitionModal/sendRecognitionView/useSendRecognitionFormStore'
import useDraftRecognitionsCount from '@tenant/recognition/RecognitionModal/useDraftRecognitionCount'
import { T } from '@transifex/react'
import { isEmpty } from 'lodash'
import { ReactNode, useEffect, useMemo, useRef } from 'react'
import MobileRecognitionDraftView from './DraftAndScheduleView/MobileRecognitionDraftView'
import MobileRecognitionPointsExceptionView from './MobileRecognitionPointsExceptionView'
import MobileRecognitionPointsView from './MobileRecognitionPointsView'
import MobileRecognitionQualitiesView from './MobileRecognitionQualitiesView'
import MobileRecognitionRecipientsView from './MobileRecognitionRecipientsView'
import MobileRecognitionScheduleView from './MobileRecognitionScheduleView'
import MobileRecognitionVisibilityView from './MobileRecognitionVisibilityView'
import MobileRecognitionSendDialog from './SendView/MobileRecognitionSendDialog'
import useMobileRecognitionDrawer, {
  MOBILE_RECOGNITION_DRAWER_KEY,
  MobileRecognitionDrawerView,
} from './useMobileRecognitionDrawer'
import useMobileRecognitionViewActions from './useMobileRecognitionViewActions'

export const MOBILE_SEND_RECOGNITION_DRAWER_KEY = 'mobileSendRecognitionDrawerKey'

export const mobileActionStyle: SxProps = {
  position: 'fixed',
  bottom: 16,
  left: 16,
  right: 16,
  display: 'flex',
  justifyContent: 'flex-start',
  zIndex: 900, // keep under the onboarding FAB (zIndex 1000)
}

const noopShowAnimation = (_show: boolean) => {
  /* no-op for mobile: animation is not used */
}

export const DEFAULT_TITLE = <T _str="Recognize someone" />

const viewTitles: Record<MobileRecognitionDrawerView, ReactNode> = {
  [MobileRecognitionDrawerView.DRAFT_AND_SCHEDULED]: DEFAULT_TITLE,
  [MobileRecognitionDrawerView.RECIPIENTS]: DEFAULT_TITLE,
  [MobileRecognitionDrawerView.VISIBILITY]: <T _str="Set post visibility" />,
  [MobileRecognitionDrawerView.QUALITIES]: <T _str="Add qualities" />,
  [MobileRecognitionDrawerView.POINTS]: <T _str="Add points" />,
  [MobileRecognitionDrawerView.SCHEDULE]: <T _str="Schedule post" />,
  [MobileRecognitionDrawerView.POINTS_EXCEPTION]: DEFAULT_TITLE,
}

const MobileRecognitionDrawer = () => {
  const { closeDrawer, dialogData, isOpen } = useMobileRecognitionDrawer()
  const { isOpen: isSendDialogOpen } = useDialog(MOBILE_SEND_RECOGNITION_DRAWER_KEY)
  const { draftRecognitionCount } = useDraftRecognitionsCount({ enabled: isOpen })
  const { qualities } = useQualities()
  const { setIsFormDirty } = useSendRecognitionFormStore()
  const currentView = dialogData?.view ?? MobileRecognitionDrawerView.DRAFT_AND_SCHEDULED
  const recognitionFromDrawer = dialogData?.recognition

  // Preserve recognition when closing the drawer to open the send dialog so the form
  // is not reset (which would clear sendTo) before the send dialog is shown.
  const recognitionRef = useRef<Recognition | undefined>(undefined)
  if (recognitionFromDrawer !== undefined) {
    recognitionRef.current = recognitionFromDrawer
  }
  const recognition =
    recognitionFromDrawer ?? (isSendDialogOpen ? recognitionRef.current : undefined)

  const viewContent: Record<MobileRecognitionDrawerView, ReactNode> = useMemo(() => {
    return {
      [MobileRecognitionDrawerView.DRAFT_AND_SCHEDULED]: <MobileRecognitionDraftView />,
      [MobileRecognitionDrawerView.RECIPIENTS]: <MobileRecognitionRecipientsView />,
      [MobileRecognitionDrawerView.VISIBILITY]: <MobileRecognitionVisibilityView />,
      [MobileRecognitionDrawerView.QUALITIES]: (
        <MobileRecognitionQualitiesView options={qualities} />
      ),
      [MobileRecognitionDrawerView.POINTS]: <MobileRecognitionPointsView />,
      [MobileRecognitionDrawerView.SCHEDULE]: <MobileRecognitionScheduleView />,
      [MobileRecognitionDrawerView.POINTS_EXCEPTION]: <MobileRecognitionPointsExceptionView />,
    }
  }, [qualities])

  const {
    form,
    isFormDirty,
    isEditMode,
    pointsExceptions,
    // setPointsExceptions,
    publishRecognition,
    handleSaveAsDraft,
    handleResetRecognitionForm,
    isPendingCreateOrSave,
  } = useSendRecognitionForm(noopShowAnimation, !isEmpty(qualities), recognition, closeDrawer)

  const viewActions = useMobileRecognitionViewActions(form)

  const submitForm = async (data: RecognitionFormData) => {
    return publishRecognition(data, !!pointsExceptions, handleResetRecognitionForm)
  }

  useEffect(() => {
    setIsFormDirty(isFormDirty)
  }, [isFormDirty, setIsFormDirty])

  useEffect(() => {
    if (!isSendDialogOpen) {
      recognitionRef.current = undefined
    }
  }, [isSendDialogOpen])

  const effectiveView = useMemo(() => {
    if (
      currentView === MobileRecognitionDrawerView.DRAFT_AND_SCHEDULED &&
      !(draftRecognitionCount?.total && draftRecognitionCount.total > 0)
    ) {
      return MobileRecognitionDrawerView.RECIPIENTS
    }
    return currentView
  }, [currentView, draftRecognitionCount?.total])

  // Keep the last view while closing so the drawer doesn't flash to different content/height during exit animation (dialogData is cleared on close).
  const lastOpenViewRef = useRef(effectiveView)
  if (isOpen) {
    lastOpenViewRef.current = effectiveView
  }
  const displayView = isOpen ? effectiveView : lastOpenViewRef.current

  const actionContent = viewActions[displayView]

  const shouldUseModalZIndex = displayView !== MobileRecognitionDrawerView.DRAFT_AND_SCHEDULED

  return (
    <FormContextWrapper formMethods={form} onSubmit={submitForm}>
      <BottomDrawer
        drawerKey={MOBILE_RECOGNITION_DRAWER_KEY}
        title={viewTitles[displayView]}
        backgroundColor={colors.grayUltraLight}
        onClose={() => closeDrawer()}
        drawerStyles={shouldUseModalZIndex ? { zIndex: theme.zIndex.modal + 1 } : undefined}
        actionButtonCount={1}>
        <Box sx={{ py: 2 }}>{viewContent[displayView]}</Box>
        {actionContent && <Box sx={mobileActionStyle}>{actionContent}</Box>}
      </BottomDrawer>
      <MobileRecognitionSendDialog
        handleResetRecognitionForm={handleResetRecognitionForm}
        hasQualities={!!qualities && qualities.length > 0}
        form={form}
        handleSaveAsDraft={handleSaveAsDraft}
        isPendingCreateOrSave={isPendingCreateOrSave}
        isEditMode={isEditMode}
      />
    </FormContextWrapper>
  )
}

export default MobileRecognitionDrawer
